import type { WalletStats } from "./wallet-card-content";
import {
  getStickerProgress,
  STICKER_REGISTRY,
  type StickerProgress,
  type WalletSticker,
} from "./wallet-stickers";

/**
 * Pure presentation helpers for the sticker collection and the showcase.
 * No DOM, no React — so the maths that decides what a card says and how it
 * moves is unit-testable.
 */

export interface StickerTone {
  /** The light end of the sticker's own gradient. */
  from: string;
  /** The saturated end. */
  to: string;
  /** The pale disc colour behind the icon. */
  accent: string;
  /** The icon colour. */
  ink: string;
}

const HEX = /#[0-9a-f]{6}\b/gi;

/**
 * The two stops of a sticker's `linear-gradient(...)` palette. Every sticker
 * in the registry is written that way; anything else falls back to the accent
 * and the ink, so a new sticker with an unusual background still renders.
 */
export function stickerTone(sticker: WalletSticker): StickerTone {
  const stops = sticker.palette.background.match(HEX) ?? [];
  return {
    from: stops[0] ?? sticker.palette.accent,
    to: stops[stops.length - 1] ?? sticker.palette.foreground,
    accent: sticker.palette.accent,
    ink: sticker.palette.foreground,
  };
}

/** `#rrggbb` plus a 0..1 alpha as an 8-digit hex colour. */
export function withAlpha(hex: string, alpha: number): string {
  const clamped = Math.max(0, Math.min(1, alpha));
  return `${hex}${Math.round(clamped * 255)
    .toString(16)
    .padStart(2, "0")}`;
}

/**
 * The trading-card number: the sticker's place in the registry, so it never
 * changes with the member's sort or filter. "07" of 36.
 */
export function stickerSerial(
  sticker: WalletSticker,
  registry: readonly WalletSticker[],
): string {
  const index = registry.findIndex((item) => item.id === sticker.id);
  const digits = String(registry.length).length;
  return String(index < 0 ? 0 : index + 1).padStart(Math.max(2, digits), "0");
}

// Rating starts every member at 1000, so 1000/1200 is a new player, not one
// who is 83% of the way there. Measure it from the starting line.
const RATING_BASELINE = 1000;

/** How close a locked sticker is, 0..1, measured from where everyone starts. */
export function stickerCloseness(
  sticker: WalletSticker,
  current: number,
): number {
  if (sticker.metric === "rating") {
    const span = sticker.threshold - RATING_BASELINE;
    return span > 0
      ? Math.max(0, Math.min(1, (current - RATING_BASELINE) / span))
      : 0;
  }
  return sticker.threshold > 0
    ? Math.max(0, Math.min(1, current / sticker.threshold))
    : 0;
}

/**
 * A stable starting angle for each sticker's iridescent rim, so a grid of
 * still stickers does not show the same rainbow in the same place 36 times.
 */
export function stickerRimAngle(stickerId: string): number {
  let hash = 0;
  for (let i = 0; i < stickerId.length; i += 1) {
    hash = (hash * 31 + stickerId.charCodeAt(i)) >>> 0;
  }
  return hash % 360;
}

export interface StickerEntry {
  sticker: WalletSticker;
  progress: StickerProgress;
  closeness: number;
}

/**
 * Every sticker with its progress, collected first, then locked ones by how
 * close they are — so the first locked entry is always the one worth chasing.
 */
export function buildStickerEntries(
  stats: WalletStats,
  registry: readonly WalletSticker[] = STICKER_REGISTRY,
): StickerEntry[] {
  return registry
    .map((sticker) => {
      const progress = getStickerProgress(sticker, stats);
      return {
        sticker,
        progress,
        closeness: stickerCloseness(sticker, progress.current),
      };
    })
    .sort((left, right) => {
      if (left.progress.unlocked !== right.progress.unlocked) {
        return left.progress.unlocked ? -1 : 1;
      }
      // Collected keep registry order; locked lead with the nearest.
      if (left.progress.unlocked) return 0;
      return right.closeness - left.closeness;
    });
}
